import {
	type RemarkProseMirrorOptions,
	toPmMark,
	toPmNode,
} from "@handlewithcare/remark-prosemirror";
import {
	type Code as MdastCode,
	type List as MdastList,
	type ListItem as MdastListItem,
} from "mdast";
import { gfmTaskListItemFromMarkdown } from "mdast-util-gfm-task-list-item";
import { gfmTaskListItem } from "micromark-extension-gfm-task-list-item";
import { normalizeUri } from "micromark-util-sanitize-uri";
import { type Processor } from "unified";
import { CONTINUE, SKIP, visit } from "unist-util-visit";

import { schema } from "../prosemirror-schema";

export function remarkTaskList(this: Processor) {
	const data = this.data();

	const micromarkExtensions = data.micromarkExtensions || (data.micromarkExtensions = []);
	const fromMarkdownExtensions =
		data.fromMarkdownExtensions || (data.fromMarkdownExtensions = []);

	micromarkExtensions.push(gfmTaskListItem());
	fromMarkdownExtensions.push(gfmTaskListItemFromMarkdown());
}

/**
 * A remark plugin that unwraps HTML nodes from their parent
 * paragraph nodes.
 */
export function remarkUnwrapHtml(this: Processor) {
	const data = this.data();

	const fromMarkdownExtensions =
		data.fromMarkdownExtensions || (data.fromMarkdownExtensions = []);

	fromMarkdownExtensions.push({
		transforms: [
			function (tree) {
				visit(tree, "paragraph", function (paragraph, index, parent) {
					if (!parent || index === undefined) {
						return CONTINUE;
					}

					if (paragraph.children.length !== 1) {
						return CONTINUE;
					}

					const firstChild = paragraph.children[0];
					if (firstChild?.type !== "html") {
						return CONTINUE;
					}

					if (firstChild.value === "<br>") {
						paragraph.children = [];
						return CONTINUE;
					}

					parent.children.splice(index, 1, firstChild);

					return SKIP;
				});
			},
		],
	});
}

// Return nothing for nodes that are ignored.
function ignore() {
	return null;
}

export const options: RemarkProseMirrorOptions = {
	schema,
	htmlHandlers: {
		img(node, _) {
			if (node.type !== "element") {
				return null;
			}
			const file = node.properties["dataFile"];
			const src = file ? null : node.properties["src"];
			const width = node.properties["width"];
			const alignment = node.properties["dataAlignment"];
			const aspectRatio =
				typeof node.properties["dataAspectRatio"] === "string"
					? parseFloat(node.properties["dataAspectRatio"])
					: undefined;
			const result = schema.nodes.image.create({
				src,
				file,
				alignment,
				width,
				aspectRatio,
			});
			return result;
		},
		br(node, parent) {
			if (node.type !== "element") {
				return null;
			}
			// For an actual hard break within a paragraph that
			// has other content, produce a hard break node
			if (parent && parent.children.length !== 1) {
				return schema.nodes.hard_break.create();
			}
			// But if this was just a top-level hard break on its
			// own, we left it as a placeholder for an empty paragraph
			return schema.nodes.paragraph.create();
		},
	},
	handlers: {
		yaml: ignore,
		definition: ignore,
		footnoteDefinition: ignore,
		paragraph: toPmNode(schema.nodes.paragraph),
		heading: toPmNode(schema.nodes.heading, (node) => ({ level: node.depth })),
		blockquote: toPmNode(schema.nodes.blockquote),
		break: toPmNode(schema.nodes.hard_break),
		code(node: MdastCode) {
			const [, cellId, cellName] = node.meta?.match(/cellId:(.*) cellName:(.*)/) ?? [];
			const child = node.value ? schema.text(node.value) : null;
			if (node.lang === "JavaScript" && cellId && cellName) {
				return schema.nodes.compute_cell.create({ cellName, id: cellId }, child);
			}
			const result = schema.nodes.code_block.create(
				{ language: node.lang, meta: node.meta },
				child
			);
			return result;
		},
		emphasis: toPmMark(schema.marks.em),
		link: toPmMark(schema.marks.link, (node) => ({
			href: normalizeUri(node.url),
			title: node.title,
		})),
		strong: toPmMark(schema.marks.strong),
		// `delete` is strikethrough in Markdown. So-called because the HTML markup is often the
		// `<del>` tag.
		delete: toPmMark(schema.marks.strike),
		inlineCode(node) {
			return schema.text(node.value, [schema.marks.code.create()]);
		},
		image: toPmNode(schema.nodes.image, (node) => ({ src: node.url })),
		list(node: MdastList, _, state) {
			return state.all(node);
		},
		listItem(node: MdastListItem, list, state) {
			if (list?.type !== "list") {
				return null;
			}
			const children = state.all(node);
			if (node.checked == null) {
				return schema.nodes.list_item.create(
					{ indent: node.data?.["depth"], bullet: node.data?.["bullet"] },
					children
				);
			}
			return schema.nodes.to_do_item.create(
				{
					status: node.checked ? "completed" : "to do",
					indent: node.data?.["depth"],
				},
				children
			);
		},
		thematicBreak: toPmNode(schema.nodes.horizontal_rule),
	},
};
